import { chunk } from 'lodash';
import { useMemo } from 'react';
import useSWR from 'swr';
import { getUserInformation } from '@/common/utils/user';
import { identityApi } from '$api-clients';
import { usePayment } from '$api-clients/hooks';

export interface PlatformFeeRow {
  identityUserId: string;
  fullName: string;
  payorName: string | null;
  isExemptFromPlatformFee: boolean;
  hasEnteredPaymentMethod: boolean | null;
  /** Total past due platform fee balance, in dollars. `0` for exempt members. */
  pastDueBalance: number;
}

interface UseGetPlatformFeeDataProps {
  viewReportAs: string;
  skip?: boolean;
}

export function useGetPlatformFeeData({
  viewReportAs,
  skip = false,
}: UseGetPlatformFeeDataProps) {
  const queryParams =
    viewReportAs && !skip ? { params: { query: { viewReportAs } } } : null;

  const {
    data: teamData,
    isLoading: isTeamLoading,
    error: teamError,
  } = usePayment('/reporting/platform-fees-status/team', queryParams, {
    shouldRetryOnError: false,
  });

  const {
    data: membersData,
    isLoading: isMembersLoading,
    error: membersError,
  } = usePayment('/reporting/platform-fees-status/team-members', queryParams, {
    shouldRetryOnError: false,
  });

  // Collect unique user IDs for name lookups:
  // - always: all member IDs
  // - isPartnerPay=true: the single team-level payorId
  // - isPartnerPay=false: each member's individual payorId
  const userIds = useMemo(() => {
    if (!membersData) return [];
    const ids = new Set<string>();
    for (const m of membersData) {
      ids.add(m.identityUserId);
      if (teamData?.isPartnerPay === false && m.payorId) {
        ids.add(m.payorId);
      }
    }
    if (teamData?.isPartnerPay && teamData.payorId) {
      ids.add(teamData.payorId);
    }
    return [...ids];
  }, [membersData, teamData]);

  const {
    data: usersInfo,
    isLoading: isUsersLoading,
    error: usersError,
  } = useSWR(
    userIds.length > 0 ? ['platform-fee-users', userIds] : null,
    async ([, ids]: [string, string[]]) => {
      const responses = await Promise.all(
        chunk(ids, 100).map((c) =>
          identityApi.GET('/users', {
            params: { query: { ids: c.join(',') } },
          }),
        ),
      );
      const mapById: Record<string, { displayName: string }> = {};
      for (const res of responses) {
        if (res.error) throw new Error('Failed to fetch user information');
        for (const user of res.data?.data ?? []) {
          mapById[user.id] = getUserInformation(user);
        }
      }
      return { mapById };
    },
    { shouldRetryOnError: false },
  );

  const rows = useMemo<PlatformFeeRow[]>(() => {
    if (!membersData) return [];

    const mappedRows = membersData.map((m) => {
      let payorName: string | null = null;
      if (!m.isExemptFromPlatformFee) {
        if (teamData?.isPartnerPay && teamData.payorId) {
          // All members share the same partner payor
          payorName = usersInfo?.mapById[teamData.payorId]?.displayName ?? null;
        } else if (teamData?.isPartnerPay === false && m.payorId) {
          // Each member has their own payor
          payorName = usersInfo?.mapById[m.payorId]?.displayName ?? null;
        }
      }

      return {
        identityUserId: m.identityUserId,
        fullName:
          usersInfo?.mapById[m.identityUserId]?.displayName ?? m.identityUserId,
        payorName,
        isExemptFromPlatformFee: m.isExemptFromPlatformFee,
        hasEnteredPaymentMethod: m.hasEnteredPaymentMethod,
        pastDueBalance: m.pastDueBalance,
      };
    });

    const seenNames = new Set<string>();
    return mappedRows.filter((row) => {
      const normalizedName = row.fullName.trim().toLowerCase();
      if (seenNames.has(normalizedName)) {
        return false;
      }
      seenNames.add(normalizedName);
      return true;
    });
  }, [membersData, teamData, usersInfo]);

  const { chargedCount, exemptCount, missingPaymentCount } = useMemo(() => {
    let charged = 0;
    let exempt = 0;
    let missingPayment = 0;
    for (const r of rows) {
      if (r.isExemptFromPlatformFee) {
        exempt += 1;
      } else if (r.hasEnteredPaymentMethod === false) {
        missingPayment += 1;
      } else {
        charged += 1;
      }
    }
    return {
      chargedCount: charged,
      exemptCount: exempt,
      missingPaymentCount: missingPayment,
    };
  }, [rows]);

  return {
    rows,
    chargedCount,
    exemptCount,
    missingPaymentCount,
    isPartnerPay: teamData?.isPartnerPay ?? null,
    isLoading: isTeamLoading || isMembersLoading || isUsersLoading,
    error: teamError ?? membersError ?? usersError,
  };
}
